import { useLazyQuery, useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { GenerateSharedAccessSignatureQuery } from '@deltek/specification-client/core/GraphQL/Queries/generateSharedAccessSignature.queries'
import {
    GenerateSharedAccessSignatureInput,
    GenerateSharedAccessSignatureResponse,
} from '@deltek/specification-client/core/Models/generateSharedAccessSignature'
import { ProfileImageInput } from '@deltek/specification-client/core/Models/profileImageInput'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { suffixDimension } from '@deltek/specification-client/core/Utilities/stringUtils'
import useCompanyBannerSizeConstants from '@deltek/specification-client/core/Widgets/Company/useCompanyBannerSizeConstants'
import {
    PickerFileMetadata,
    PickerResponse,
} from '@deltek/specification-client/core/Widgets/Filestack/FilestackButton'
import ImageFilestackButton from '@deltek/specification-client/core/Widgets/Filestack/ImageFilestackButton'
import SpecImage from '@deltek/specification-client/core/Widgets/SpecImage'
import { Theme, makeStyles } from '@material-ui/core/styles'
import AddAPhotoIcon from '@material-ui/icons/AddAPhoto'
import Skeleton from '@material-ui/lab/Skeleton'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import React, { useEffect, useState } from 'react'
import CircularPencilButton from './CircularPencilButton'

const companyBannerEditStyles = makeStyles((theme: Theme) => ({
    editButton: {
        position: 'absolute',
        marginTop: theme.spacing(2),
        marginRight: theme.spacing(2),
        right: theme.spacing(0.5),
    },
    imageWidthPosition: {
        width: '100%',
        position: 'absolute',
    },
    imageObjectFit: {
        objectFit: 'fill',
    },
    companyImageButtonLabel: {
        flexDirection: 'column',
    },
    inheritHeight: {
        height: 'inherit',
    },
    addCompanyImagePlaceholder: {
        backgroundColor: '#A2CDDD',
        color: '#5E5F61',
        textAlign: 'center',
    },
}))

export interface CompanyBannerEditProps {
    bannerUrl?: string
    companyBannerFileInTransit: boolean
    handleCompanyBannerFileInTransit: (imageRendering: boolean) => void
    handleCompanyBannerChange?: (
        companyBannerImageInput: ProfileImageInput
    ) => void
    handleFilestackLoading?: (filestackLoading: boolean | null) => void
}

const CompanyBannerEdit: React.FC<CompanyBannerEditProps> = ({
    bannerUrl,
    companyBannerFileInTransit,
    handleCompanyBannerChange,
    handleCompanyBannerFileInTransit,
    handleFilestackLoading,
}) => {
    const classes = companyBannerEditStyles()
    const {
        isXs,
        isGreaterThanXl,
        computedDimension,
        theme,
        resizeDimensions,
    } = useCompanyBannerSizeConstants()
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)
    const [imageFile, setImageFile] = useState<PickerFileMetadata>()
    const [fullImagePath, setFullImagePath] = useState<string>()
    const [computedImageSrc, setComputedImageSrc] = useState<string | null>(
        null
    )
    const [
        generateSharedAccessSignature,
        { loading: sasLoading, error: sasError, data: sasData },
    ] = useLazyQuery<
        GenerateSharedAccessSignatureResponse,
        GenerateSharedAccessSignatureInput
    >(GenerateSharedAccessSignatureQuery, {
        fetchPolicy: 'no-cache',
    })
    const [specImageLoading, setSpecImageLoading] = useState<boolean>(false)

    const onFilepickerDone = (pickerResponse: PickerResponse) => {
        if (pickerResponse.filesFailed.length) {
            showGlobalSnackbar?.({
                variables: {
                    id: 'CompanyBannerFileUploadError',
                    message: 'Error uploading company banner.',
                    variant: 'error',
                },
            })
            return
        }

        setComputedImageSrc('')
        generateSharedAccessSignature({
            variables: {
                input: {
                    durationInMinutes: 5,
                    storageAccountName:
                        processEnv.REACT_APP_BlobStorageAccountName,
                },
            },
        })
        setImageFile(pickerResponse.filesUploaded[0])
    }

    useEffect(() => {
        if (!sasError) return
        showGlobalSnackbar({
            variables: {
                id: 'CompanyBannerEditSas',
                message: 'Error getting shared access signature.',
                variant: 'error',
            },
        })
    }, [sasError, showGlobalSnackbar])

    useEffect(() => {
        if (!imageFile || !sasData) return
        const imagePath = `${processEnv.REACT_APP_BlobStorageUrl}/${
            processEnv.REACT_APP_BlobStorageUnverifiedContainerName
        }/${encodeURIComponent(imageFile.key!)}${
            sasData.generateSharedAccessSignature.sharedAccessSignature
        }`
        setFullImagePath(imagePath)
        handleCompanyBannerChange?.({
            fileName: imageFile.filename,
            key: imageFile.key!,
            fileSize: imageFile.size,
            resizeDimensions,
        })
    }, [imageFile, sasData, handleCompanyBannerChange, resizeDimensions])

    useEffect(() => {
        if (fullImagePath) {
            setComputedImageSrc(fullImagePath)
            return
        }

        if (bannerUrl) {
            setComputedImageSrc(
                suffixDimension({
                    filename: bannerUrl,
                    dimension: computedDimension,
                })
            )
        }
    }, [bannerUrl, fullImagePath, computedDimension])

    return (
        <div
            style={{
                marginLeft: isGreaterThanXl ? 0 : -theme.spacing(3),
                marginRight: isGreaterThanXl
                    ? 0
                    : isXs
                    ? -theme.spacing(2)
                    : -theme.spacing(3),
                height: computedDimension.height,
                position: 'relative',
            }}
            data-testid="companyBannerEditContainer"
        >
            {(companyBannerFileInTransit || sasLoading || specImageLoading) && (
                <Skeleton
                    data-testid="companyBannerEditSkeleton"
                    variant="rect"
                    height={computedDimension.height}
                    className={classes.imageWidthPosition}
                />
            )}
            {!companyBannerFileInTransit && !sasLoading && (
                <>
                    {computedImageSrc !== null ? (
                        <SpecImage
                            alt="uploaded banner"
                            src={computedImageSrc}
                            additionalClassNames={clsx(
                                classes.imageWidthPosition,
                                classes.imageObjectFit
                            )}
                            data-testid="companyBannerEditImg"
                            height={computedDimension.height}
                            handleImageLoading={setSpecImageLoading}
                        />
                    ) : (
                        <div
                            className={clsx(
                                classes.addCompanyImagePlaceholder,
                                classes.imageWidthPosition,
                                classes.inheritHeight
                            )}
                        >
                            <GridContainer
                                direction="column"
                                justifyContent="center"
                                alignItems="center"
                                className={classes.inheritHeight}
                            >
                                <GridItem>
                                    <AddAPhotoIcon />
                                    <br />
                                    Add Company Image
                                </GridItem>
                            </GridContainer>
                        </div>
                    )}
                </>
            )}

            {/* The pencil button on the upper right */}
            <ImageFilestackButton
                apiKey={processEnv.REACT_APP_FileStack_ApiKey}
                kpCoreUri={processEnv.REACT_APP_KPCoreUri}
                cropDimension={[1920, 200]}
                onFilepickerDone={onFilepickerDone}
                showGlobalSnackbar={showGlobalSnackbar}
                handleLoadingFilestack={handleFilestackLoading}
                handleTransferringFiles={handleCompanyBannerFileInTransit}
                renderButton={(onClick: (event: React.MouseEvent) => void) => (
                    <CircularPencilButton
                        ariaLabel="Upload cover image button"
                        data-testid="editBannerImageContainer"
                        containerClassName={classes.editButton}
                        hide={
                            companyBannerFileInTransit ||
                            sasLoading ||
                            specImageLoading
                        }
                        onClick={onClick}
                    />
                )}
            />
        </div>
    )
}

export default CompanyBannerEdit
